// @intent: PixiJS v8 asset access wrapper for the runtime adapter
// @flow: RuntimeCanvas asks preloadPixiAssets -> PixiLayerRenderer reads textures with getPixiTexture
// @stale: update when Pixi Assets API or cache semantics change

import { Assets, loadTextures, type LoadTextureConfig, type Texture, type UnresolvedAsset } from 'pixi.js';

const LOGICAL_APP_ASSET_FILE_URL_RE = /^(?:https?:\/\/[^/]+)?\/api\/v1\/assets\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/file$/i;
const RELATIVE_LOGICAL_APP_ASSET_FILE_URL_RE = /^\/api\/v1\/assets\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/file$/i;

export function isLogicalPixiAssetFileUrl(url: string): boolean {
  return LOGICAL_APP_ASSET_FILE_URL_RE.test(url);
}

export function resolvePixiAssetUrl(url: string, assetBaseUrl: string | undefined): string {
  if (!assetBaseUrl || !RELATIVE_LOGICAL_APP_ASSET_FILE_URL_RE.test(url)) return url;
  return new URL(url, assetBaseUrl).toString();
}

function createLogicalAssetLoadEntry(url: string): UnresolvedAsset | null {
  if (!isLogicalPixiAssetFileUrl(url)) return null;
  return {
    alias: url,
    src: url,
    parser: 'texture',
  };
}

async function loadLogicalAssets(logicalAssets: readonly UnresolvedAsset[]): Promise<void> {
  const previousConfig: LoadTextureConfig = {
    crossOrigin: loadTextures.config?.crossOrigin ?? 'anonymous',
    preferCreateImageBitmap: loadTextures.config?.preferCreateImageBitmap ?? true,
    preferWorkers: loadTextures.config?.preferWorkers ?? true,
  };
  loadTextures.config = {
    ...previousConfig,
    crossOrigin: 'use-credentials',
    preferCreateImageBitmap: false,
    preferWorkers: false,
  };
  try {
    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await Assets.load(logicalAssets);
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  } finally {
    loadTextures.config = previousConfig;
  }
}

export async function preloadPixiAssets(urls: readonly string[]): Promise<void> {
  if (urls.length === 0) return;
  const directUrls: string[] = [];
  const logicalAssets: UnresolvedAsset[] = [];

  for (const url of new Set(urls)) {
    const logicalAsset = createLogicalAssetLoadEntry(url);
    if (logicalAsset) {
      logicalAssets.push(logicalAsset);
    } else {
      directUrls.push(url);
    }
  }

  if (directUrls.length > 0) await Assets.load(directUrls);
  if (logicalAssets.length > 0) await loadLogicalAssets(logicalAssets);
}

export function getPixiTexture(url: string): Texture | null {
  if (!Assets.cache.has(url)) return null;
  const texture = Assets.get<Texture>(url) as Texture | undefined;
  return texture ?? null;
}
